/**
 * Which whole-hour duration chips the slot sheet offers for a pitch.
 *
 * Two bounds stack. The club's `Booking_MinMinutes`/`MaxMinutes` policy comes first — the server
 * enforces those, so a chip outside them would be a button that 400s. On top sits a per-format
 * floor, which can only ever REMOVE chips the policy already allows.
 */

/**
 * Shortest bookable slot per format, where a whole hour is not enough.
 *
 * 11v11 is priced at half the game rate per hour (£45 summer / £63.50 winter) precisely because
 * a game occupies two hours — no 11v11 fixture fits inside one (70 minutes at U13, 90 from U17),
 * and a 1-hour booking would take half a game's fee for a slot no game can be played in. Every
 * other format plays inside the hour, so the policy minimum stands.
 *
 * Unlike the other bounds this sheet mirrors, this one is ENFORCED: the server applies the same
 * floor from `BookingFormatRules.MinBookingMinutes` on create, quote and block booking. Keep the
 * two in step — a value only here offers a chip the server refuses, and a value only there hides
 * a slot coaches are allowed to book.
 */
export const FORMAT_MIN_MINUTES: Record<string, number> = {
  ElevenVEleven: 120,
};

export interface DurationOptions {
  /** The chips to render, shortest first. Never empty. */
  minutes: number[];
  /**
   * The shortest slot still on offer, set only when this format's floor is what removed the
   * shorter chips — the sheet quotes it back to explain the missing options. Null when the club
   * policy alone shaped the list, so a club whose own minimum is already 2 hours gets no
   * redundant explanation.
   *
   * Deliberately the first surviving chip rather than the raw floor: they coincide while every
   * floor is a whole number of hours, but a 100-minute floor would otherwise reach the sheet's
   * `fmtDuration` and render as "1.6666666666666667 hours".
   */
  formatFloorMinutes: number | null;
}

export function bookingDurations(
  format: string,
  minMinutes: number,
  maxMinutes: number,
): DurationOptions {
  const first = Math.max(60, Math.ceil(minMinutes / 60) * 60);
  const last = Math.floor(maxMinutes / 60) * 60;
  const policy: number[] = [];
  for (let m = first; m <= last; m += 60) policy.push(m);

  // Bounds so narrow that rounding empties the range would leave the sheet with no chips at all,
  // so fall back to one hour and let the server have the final word.
  if (policy.length === 0) return { minutes: [60], formatFloorMinutes: null };

  // Object.hasOwn rather than `?? 0`: indexing an object literal with a bare string reaches
  // Object.prototype, so a format of "constructor" or "toString" returns a function — which is
  // not nullish, so `??` would not fire and the comparison below would silently drop every chip.
  const floor = Object.hasOwn(FORMAT_MIN_MINUTES, format) ? FORMAT_MIN_MINUTES[format] : 0;
  const allowed = policy.filter((m) => m >= floor);

  // A floor that would empty the sheet is ignored rather than obeyed: a club that has capped
  // bookings below it has said something the server will enforce, and offering an unbookable
  // chip is worse than offering a short one.
  if (allowed.length === 0 || allowed.length === policy.length) {
    return { minutes: policy, formatFloorMinutes: null };
  }

  return { minutes: allowed, formatFloorMinutes: allowed[0] };
}
